import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Estimate, useGlassContext } from '@/context/GlassContext';
import { formatCurrency, useCalculateTotals, formatWeight, formatArea, calculateItemCost } from '@/utils/calculationUtils';
import { formatDimension } from '@/utils/measurementUtils';
import { GlassTypeCache } from '@/services/GlassTypeCache';
// PDF generation imports removed
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MeasurementOptions } from './MeasurementToggleOptions';
import ImageUploader from './ImageUploader';
import { EstimateImage } from '@/services/imageService';

interface EstimateViewModalProps {
  estimate: (Estimate & { images?: EstimateImage[] }) | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EstimateViewModal: React.FC<EstimateViewModalProps> = ({
  estimate,
  open,
  onOpenChange,
}) => {
  const { glassTypes } = useGlassContext();

  // Get settings from the estimate or use defaults
  const settings = estimate?.settings || {
    taxRate: 8.25,
    surchargeRate: 0,
    markupRate: 0,
    shippingRate: 15,
    otherFee: 0,
    roundUpGlassSize: false
  };

  // Ensure roundUpGlassSize is included in settings
  // Use type assertion to avoid TypeScript errors
  const extendedSettings = settings as typeof settings & { roundUpGlassSize?: boolean };
  if (extendedSettings.roundUpGlassSize === undefined) {
    extendedSettings.roundUpGlassSize = false;
  }

  // Log the estimate and settings for debugging
  console.log('Estimate in view modal:', estimate);
  console.log('Settings in view modal:', settings);

  // Log images for debugging
  if (estimate?.images) {
    console.log(`Estimate has ${estimate.images.length} images:`,
      estimate.images.map(img => ({
        id: img.id,
        fileName: img.fileName,
        hasDataUrl: !!img.dataUrl,
        dataUrlLength: img.dataUrl ? img.dataUrl.length : 0
      }))
    );
  }

  const { subtotal, shipping, surcharge, tax, total, weight, area } = useCalculateTotals(
    estimate?.items || [],
    glassTypes,
    {
      shippingPercentage: settings.shippingRate,
      surchargePercentage: settings.surchargeRate,
      taxPercentage: settings.taxRate,
      // @ts-ignore - Add roundUpGlassSize setting
      roundUpGlassSize: extendedSettings.roundUpGlassSize || false
    }
  );

  // Log the calculated values for debugging
  console.log('Calculated values:', { subtotal, shipping, surcharge, tax, total });

  if (!estimate) {
    return null;
  }

  // PDF generation functionality removed

  // Helper function to determine measurement options text
  const getMeasurementOptionsText = (options?: MeasurementOptions): string => {
    if (!options) return "No measurement options specified";

    let text = `Glass Thickness: ${options.thickness}, Measurement Type: ${options.measurementType}, System: ${options.system}`;

    return text;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{estimate.name}</DialogTitle>
          <DialogDescription>
            View and manage your glass estimate details
          </DialogDescription>
          <div className="text-sm text-muted-foreground">
            Created: {new Date(estimate.created).toLocaleDateString()}
          </div>
          {estimate.measurementOptionsSets && estimate.measurementOptionsSets.length > 0 && (
            <div className="mt-2 p-2 bg-primary/10 rounded-md text-sm">
              <span className="font-medium">Measurement Options:</span> {getMeasurementOptionsText(estimate.measurementOptionsSets[0])}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Estimate Details</h3>
          </div>

          <div className="space-y-8">
            {/* Group items by measurement options set */}
            {estimate.measurementOptionsSets && estimate.measurementOptionsSets.length > 0 ? (
              estimate.measurementOptionsSets.map((optionsSet) => {
                // Find items for this options set
                const itemsForSet = estimate.items.filter(item => item.measurementOptionsId === optionsSet.id);

                // Skip if no items in this set
                if (itemsForSet.length === 0) return null;

                let globalItemIndex = 1;

                return (
                  <div key={optionsSet.id} className="space-y-2">
                    <h3 className="text-md font-medium text-primary">{optionsSet.name}</h3>
                    <div className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Width</TableHead>
                            <TableHead>Height</TableHead>
                            <TableHead>Glass Type</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Area</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead className="text-right">Weight</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {itemsForSet.map((item, index) => {
                            // Get the glass type, preserving original properties if available
                            const originalGlassType = typeof item.glassType === 'object' ? item.glassType : null;
                            const glassTypeId = typeof item.glassType === 'string' ? item.glassType : (originalGlassType?.id || '');

                            // If we have a complete glass type object with cost information, use it directly
                            let glassType: any;
                            let itemWithGlassType: any;

                            if (originalGlassType &&
                                ((originalGlassType as any).costPerSqFt || (originalGlassType as any).price) &&
                                ((originalGlassType as any).weightPerSqFt || (originalGlassType as any).lbsPerSqFt)) {

                                // Use the original glass type with normalized property names
                                glassType = {
                                    ...originalGlassType,
                                    costPerSqFt: (originalGlassType as any).costPerSqFt || (originalGlassType as any).price,
                                    weightPerSqFt: (originalGlassType as any).weightPerSqFt || (originalGlassType as any).lbsPerSqFt
                                };

                                itemWithGlassType = {
                                    ...item,
                                    glassType: glassType
                                };
                            } else {
                                // Use the GlassTypeCache as a fallback
                                glassType = GlassTypeCache.getOrRestoreGlassType(glassTypeId, glassTypes, item.thickness, originalGlassType);

                                itemWithGlassType = {
                                    ...item,
                                    glassType: glassType
                                };
                            }

                            // Calculate costs using the item with the correct glass type
                            const { cost, weight, area } = calculateItemCost(itemWithGlassType, glassTypes);

                            return (
                              <TableRow key={index}>
                                <TableCell>{globalItemIndex++}</TableCell>
                                <TableCell>{formatDimension(item.adjustedWidth || item.width)}</TableCell>
                                <TableCell>{formatDimension(item.adjustedHeight || item.height)}</TableCell>
                                <TableCell>{glassType?.name || 'Unknown'}</TableCell>
                                <TableCell className="text-right">{item.quantity}</TableCell>
                                <TableCell className="text-right">{formatArea(area)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(cost)}</TableCell>
                                <TableCell className="text-right">{formatWeight(weight)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback to the original behavior if no measurement options sets
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Width</TableHead>
                      <TableHead>Height</TableHead>
                      <TableHead>Glass Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Area</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Weight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {estimate.items.map((item, index) => {
                      // Get the glass type, preserving original properties if available
                      const originalGlassType = typeof item.glassType === 'object' ? item.glassType : null;
                      const glassTypeId = typeof item.glassType === 'string' ? item.glassType : (originalGlassType?.id || '');

                      // If we have a complete glass type object with cost information, use it directly
                      let glassType: any;
                      let itemWithGlassType: any;

                      if (originalGlassType &&
                          ((originalGlassType as any).costPerSqFt || (originalGlassType as any).price) &&
                          ((originalGlassType as any).weightPerSqFt || (originalGlassType as any).lbsPerSqFt)) {

                          // Use the original glass type with normalized property names
                          glassType = {
                              ...originalGlassType,
                              costPerSqFt: (originalGlassType as any).costPerSqFt || (originalGlassType as any).price,
                              weightPerSqFt: (originalGlassType as any).weightPerSqFt || (originalGlassType as any).lbsPerSqFt
                          };

                          itemWithGlassType = {
                              ...item,
                              glassType: glassType
                          };
                      } else {
                          // Use the GlassTypeCache as a fallback
                          glassType = GlassTypeCache.getOrRestoreGlassType(glassTypeId, glassTypes, item.thickness, originalGlassType);

                          itemWithGlassType = {
                              ...item,
                              glassType: glassType
                          };
                      }

                      // Calculate costs using the item with the correct glass type
                      const { cost, weight, area } = calculateItemCost(itemWithGlassType, glassTypes);

                      return (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{formatDimension(item.adjustedWidth || item.width)}</TableCell>
                          <TableCell>{formatDimension(item.adjustedHeight || item.height)}</TableCell>
                          <TableCell>{glassType?.name || 'Unknown'}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatArea(area)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(cost)}</TableCell>
                          <TableCell className="text-right">{formatWeight(weight)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row justify-between p-4 bg-card/30 rounded-lg">
            <div className="space-y-1">
              <p>Total Area: {formatArea(area)}</p>
              <p>Total Weight: {formatWeight(weight)}</p>
            </div>
            <div className="space-y-1 mt-4 md:mt-0">
              <p>Subtotal: {formatCurrency(subtotal)}</p>
              <p>Shipping ({settings.shippingRate}%): {formatCurrency(shipping)}</p>
              {surcharge > 0 && (
                <p>Surcharge ({settings.surchargeRate}%): {formatCurrency(surcharge)}</p>
              )}
              <p>Tax ({settings.taxRate}%): {formatCurrency(tax)}</p>
              <p className="font-bold">Grand Total: {formatCurrency(total)}</p>
            </div>
          </div>

          {/* Images Section */}
          {estimate.images && estimate.images.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-4">Attached Images</h3>
              <ImageUploader
                images={estimate.images}
                onAddImages={() => {}}
                onRemoveImage={() => {}}
                onUpdateImageDescription={() => {}}
                readOnly={true}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EstimateViewModal;
